import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    fetchLatestComments,
    fetchBrandVoiceGraph,
    fetchFollowersGraph,
    fetchWeeklyStats,
} from '../../shared/api/dashboardApi';
import {
    Comment,
    BrandVoiceGraph,
    FollowersGraph,
    WeeklyStats,
} from '../../shared/types/dashboardTypes';

export interface DashboardState {
    comments: Comment[];
    brandVoiceGraph: BrandVoiceGraph | null;
    followersGraph: FollowersGraph | null;
    weeklyStats: WeeklyStats | null;
    loading: boolean;
    error: string | null;
}

export const initialState: DashboardState = {
    comments: [],
    brandVoiceGraph: null,
    followersGraph: null,
    weeklyStats: null,
    loading: false,
    error: null,
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        clearComments: (state) => {
            state.comments = [];
        },
        clearBrandVoiceGraph: (state) => {
            state.brandVoiceGraph = null;
        },
        clearFollowersGraph: (state) => {
            state.followersGraph = null;
        },
        clearWeeklyStats: (state) => {
            state.weeklyStats = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Latest Comments
            .addCase(fetchLatestComments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLatestComments.fulfilled, (state, action: PayloadAction<Comment[]>) => {
                state.comments = action.payload;
                state.loading = false;
            })
            .addCase(fetchLatestComments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch latest comments';
            })
            // Brand Voice Graph
            .addCase(fetchBrandVoiceGraph.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchBrandVoiceGraph.fulfilled,
                (state, action: PayloadAction<BrandVoiceGraph>) => {
                    state.brandVoiceGraph = action.payload;
                    state.loading = false;
                },
            )
            .addCase(fetchBrandVoiceGraph.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch brand voice graph';
            })
            // Followers Graph
            .addCase(fetchFollowersGraph.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchFollowersGraph.fulfilled,
                (state, action: PayloadAction<FollowersGraph>) => {
                    state.followersGraph = action.payload;
                    state.loading = false;
                },
            )
            .addCase(fetchFollowersGraph.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch followers graph';
            })
            // Weekly Stats
            .addCase(fetchWeeklyStats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWeeklyStats.fulfilled, (state, action: PayloadAction<WeeklyStats>) => {
                state.weeklyStats = action.payload;
                state.loading = false;
            })
            .addCase(fetchWeeklyStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch weekly stats';
            });
    },
});

export const { clearComments, clearBrandVoiceGraph, clearFollowersGraph, clearWeeklyStats } =
    dashboardSlice.actions;
export default dashboardSlice.reducer;
